import { INotificationSettings, IOrganiserMarketingConsent, IPrivacySettings, IUserProfileRequest, IUserProfileResponse, OrganiserMarketingChannel } from '../interfaces/IUserProfile';
import { AxiosInstance } from 'axios';

export interface IAccountDeletionBlocker {
    code: string;
    message: string;
    companyId?: string;
    companyName?: string;
    count: number;
}

export interface IAccountDeletionImpact {
    canRequestDeletion: boolean;
    isPendingDeletion: boolean;
    deletionRequestedAtUtc?: string;
    scheduledDeletionAtUtc?: string;
    blockers: IAccountDeletionBlocker[];
}

export interface IAccountDeletionRequestResult {
    deletionRequestedAtUtc: string;
    scheduledDeletionAtUtc: string;
}

export interface IAccountDeletionImmediateResult {
    deletedAtUtc: string;
}

export class ProfileService {
    private apiClient: AxiosInstance;
    private endpoint: string;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
        this.endpoint = '/profile';
    }

    async getProfile(): Promise<IUserProfileResponse> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}`);
            return response.data as IUserProfileResponse;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getProfileImage(): Promise<string> {
        try {
            const response = await this.apiClient.get<IUserProfileResponse>(`${this.endpoint}`);
            return response.data?.imageUrl ?? '';
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async updateProfile(profile: IUserProfileRequest): Promise<any> {
        try {
            const response = await this.apiClient.put(`${this.endpoint}`, profile);
            return response.data as IUserProfileResponse;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async setAllowConnections(allowConnections: boolean): Promise<void> {
        try {
            await this.apiClient.patch(`${this.endpoint}/allow-connections`, allowConnections, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async updateProfileImage(file: File): Promise<void> {
        try {
            const formData = new FormData();
            formData.append('photo', file);

            await this.apiClient.post(`${this.endpoint}/image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async deleteProfileImage(): Promise<any> {
        try {
            const response = await this.apiClient.delete(`${this.endpoint}/image`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async patchNotificationSettings(settings: INotificationSettings): Promise<void> {
        try {
            await this.apiClient.patch(this.endpoint, { notificationSettings: settings });
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async patchPrivacySettings(settings: IPrivacySettings): Promise<void> {
        try {
            await this.apiClient.patch(this.endpoint, { privacySettings: settings });
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getOrganiserMarketingConsents(): Promise<IOrganiserMarketingConsent[]> {
        try {
            const response = await this.apiClient.get<IOrganiserMarketingConsent[]>(`${this.endpoint}/organiser-marketing`);
            return response.data ?? [];
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    /** Grants or withdraws marketing consent for one organiser and channel; returns the refreshed list. */
    async setOrganiserMarketingConsent(
        companyId: string,
        channel: OrganiserMarketingChannel,
        granted: boolean,
        wordingHash?: string,
    ): Promise<IOrganiserMarketingConsent[]> {
        try {
            const response = await this.apiClient.put<IOrganiserMarketingConsent[]>(
                `${this.endpoint}/organiser-marketing/${companyId}/${channel}`,
                { granted, channel: 'web', wordingHash: granted ? wordingHash : undefined },
            );
            return response.data ?? [];
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getAccountDeletionImpact(): Promise<IAccountDeletionImpact> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/deletion-impact`);
            return response.data as IAccountDeletionImpact;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || 'An error occurred');
        }
    }

    async requestAccountDeletion(): Promise<IAccountDeletionRequestResult> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/delete-request`);
            return response.data as IAccountDeletionRequestResult;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || 'An error occurred');
        }
    }

    async cancelAccountDeletion(): Promise<void> {
        try {
            await this.apiClient.post(`${this.endpoint}/delete-cancel`);
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || 'An error occurred');
        }
    }

    async deleteAccountNow(confirmationText: string): Promise<IAccountDeletionImmediateResult> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/delete-now`, { confirmationText });
            return response.data as IAccountDeletionImmediateResult;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || 'An error occurred');
        }
    }
}